import { useState } from 'react';
import {
  CategoryUnionType,
  CompanyLinkType,
  CompanyTagType,
  CompanyTagUnionType,
  CompanyWithSitesMinifiedRowType,
} from '@api';
import {
  AddCompanyFormSchemaType,
  AddCompanyModal,
  AddCompanySiteFormSchemaType,
  AddCompanySiteModal,
  CompanyLinkForm,
  CompanyLinkFormValuesType,
  useCreateCompany,
  useCreateCompanySite,
} from '@common';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { EditFreelancerForm, FreelancerFormSchemaType } from './components';
import { useModalTitle } from './hooks';

export type OpenedModalUnionType =
  | 'ADD_COMPANY_LINK'
  | 'ADD_FREELANCER'
  | 'EDIT_COMPANY_LINK'
  | 'EDIT_FREELANCER';

type CompanyLinksModalPropsType = {
  companyTags: CompanyTagType[];
  openedModal: OpenedModalUnionType | null;
  onClose: () => void;
  handleCreateCompanyLink: (data: CompanyLinkFormValuesType) => void;
  handleEditAndAddFreelancer: (data: FreelancerFormSchemaType) => void;
  handleEditCompanyLink: (data: CompanyLinkFormValuesType) => void;
  companySites: CompanyWithSitesMinifiedRowType[];
  companyLinkCategories: CategoryUnionType[];
  companyLinkToEdit: CompanyLinkType | null;
};

export const CompanyLinksModal: React.FC<CompanyLinksModalPropsType> = ({
  companyTags,
  openedModal,
  onClose,
  handleCreateCompanyLink,
  handleEditAndAddFreelancer,
  handleEditCompanyLink,
  companySites,
  companyLinkCategories,
  companyLinkToEdit,
}) => {
  const title = useModalTitle(openedModal);
  const [preselectCompanySiteId, setPreselectCompanySiteId] = useState<
    string | null
  >(null);
  const [siteCompanyId, setSiteCompanyId] = useState<string | null>(null);
  const [
    addCompanyModalOpened,
    { open: openAddCompanyModal, close: closeAddCompanyModal },
  ] = useDisclosure(false);
  const [
    addCompanySiteModalOpened,
    { open: openAddCompanySiteModal, close: closeAddCompanySiteModal },
  ] = useDisclosure(false);

  const { submitNewCompany } = useCreateCompany();
  const { createCompanySite } = useCreateCompanySite(siteCompanyId ?? '');

  const addSiteCompanyName =
    siteCompanyId !== null
      ? (companySites.find((c) => c.id === siteCompanyId)?.name ?? '')
      : '';

  const handleAddCompanySite = (companyId: string) => {
    setSiteCompanyId(companyId);
    openAddCompanySiteModal();
  };

  const handleSubmitNewCompany = async (
    data: AddCompanyFormSchemaType
  ): Promise<void> => {
    const newCompany = await submitNewCompany({
      ...data,
      categories: data.categories as CompanyTagUnionType[],
      address: { ...data.address, cityEnglish: null, state: null },
      contact: { ...data.contact, mobile: null },
    });
    setPreselectCompanySiteId(newCompany.headquarter.id);
  };

  const handleSubmitNewCompanySite = async (
    formValues: AddCompanySiteFormSchemaType
  ): Promise<void> => {
    const newSite = await createCompanySite({
      address: { ...formValues.address, cityEnglish: null, state: null },
      contact: { ...formValues.contact, mobile: null },
    });
    setPreselectCompanySiteId(newSite.id);
  };

  return (
    <>
      <Modal opened={!!openedModal} onClose={onClose} title={title} size="lg">
        {openedModal === 'ADD_COMPANY_LINK' && (
          <CompanyLinkForm.Root
            companyLink={undefined}
            handleSubmit={handleCreateCompanyLink}
            onCancel={onClose}
          >
            <CompanyLinkForm.BusinessFields.Root>
              <CompanyLinkForm.BusinessFields.SiteSelect
                companySites={companySites}
                handleAddCompany={openAddCompanyModal}
                handleAddCompanySite={handleAddCompanySite}
                preselectSiteId={preselectCompanySiteId}
              />
              <CompanyLinkForm.BusinessFields.JobTitle />
              <CompanyLinkForm.BusinessFields.BuyerId />
            </CompanyLinkForm.BusinessFields.Root>
            <CompanyLinkForm.ContactFields />
            <CompanyLinkForm.Categories
              companyLinkCategories={companyLinkCategories}
            />
          </CompanyLinkForm.Root>
        )}
        {openedModal === 'ADD_FREELANCER' && (
          <EditFreelancerForm
            onCancel={onClose}
            companyLinkCategories={companyLinkCategories}
            handleSubmit={handleEditAndAddFreelancer}
          />
        )}
        {openedModal === 'EDIT_COMPANY_LINK' && companyLinkToEdit && (
          <CompanyLinkForm.Root
            companyLink={companyLinkToEdit}
            handleSubmit={handleEditCompanyLink}
            onCancel={onClose}
          >
            <CompanyLinkForm.BusinessFields.Root>
              <CompanyLinkForm.BusinessFields.SiteSelect
                companySites={companySites}
                handleAddCompany={openAddCompanyModal}
                handleAddCompanySite={handleAddCompanySite}
                preselectSiteId={preselectCompanySiteId}
              />
              <CompanyLinkForm.BusinessFields.JobTitle />
              <CompanyLinkForm.BusinessFields.BuyerId />
            </CompanyLinkForm.BusinessFields.Root>
            <CompanyLinkForm.ContactFields />
            <CompanyLinkForm.Categories
              companyLinkCategories={companyLinkCategories}
            />
          </CompanyLinkForm.Root>
        )}
        {openedModal === 'EDIT_FREELANCER' && companyLinkToEdit && (
          <EditFreelancerForm
            freelancer={companyLinkToEdit}
            onCancel={onClose}
            companyLinkCategories={companyLinkCategories}
            handleSubmit={handleEditAndAddFreelancer}
          />
        )}
      </Modal>
      <AddCompanyModal
        opened={addCompanyModalOpened}
        onClose={closeAddCompanyModal}
        onSubmit={handleSubmitNewCompany}
        companyTags={companyTags}
      />
      <AddCompanySiteModal
        opened={addCompanySiteModalOpened}
        onClose={closeAddCompanySiteModal}
        onSubmit={handleSubmitNewCompanySite}
        companyName={addSiteCompanyName}
      />
    </>
  );
};
